// @ts-nocheck
import React, {useEffect, useCallback, useState} from "react";
import {NotificationManager} from 'react-notifications';
import styled from 'styled-components';
import { AdminTabs } from "./AdminTabs";
import { useApi } from "../../lib/api";
import useAsync from "../../lib/use-async";
import Button from "../../components/Button";
import ListScreen from "../../components/ListScreen";
import Loading from "../../components/Loading";
import Notification from "../../components/Notification";
import Error from "../../components/Error";
import SideModal from "../../components/SideModal";
import { Link, Route } from "react-router-dom";
import Form from "../../components/Form";
import useToggle from "../../lib/use-toggle";
import Input from "../../components/Input";
import Role, {getRoleHelpText, getRoleOptions, getRoleFriendlyName} from "../../lib/Role";
import { useUser } from "../../lib/user";
import ChipInput from "../../components/ChipInput";
import ListItem from "../../components/ListItem";
import {T} from '../../components/I18n';
import Icon from '../../components/Icon';
import TimeAgo from "../../components/TimeAgo";
import { useUrlState } from "../../lib/with-url-state";
import search from "../../lib/search";
import {alphaSort} from '../../lib/sort';
import { credentials } from "../../state/reducers";
import * as validation from '../../lib/validation';
import * as multiAz from '../../lib/multi-az';

type UserGroup = {
  id: string;
};

type ServiceAccount = {
  userName: string;
  role: keyof Role;
  groups: UserGroup[];
};

export function ServiceAccountsTab() {
  const api = useApi();
  const [{filter}, setUrlState] = useUrlState({filter: ''});
  const [getServiceAccounts, {loading, error, result}] = useAsync(api.getServiceAccounts);
  const [serviceAccounts, setServiceAccounts] = useState([]);

  useEffect(() => {
    getServiceAccounts();
  }, []);

  useEffect(() => {
    setServiceAccounts((result || {}).content || []);
  }, [result]);

  const handleSave = useCallback((serviceAccount: ServiceAccount) => {
    setServiceAccounts(serviceAccounts => serviceAccounts.filter(s => s.id !== serviceAccount.id).concat([serviceAccount]));
  }, []);

  const handleSearch = useCallback((e) => {
    setUrlState({filter: e.target.value});
  }, []);

  const handleRotateKey = useCallback(async (serviceAccount) => {
    if (!window.confirm(`Are you sure you want to rotate the API key for ${serviceAccount.userName}? The current API key will no longer be valid.`)) {
      return;
    }

    try {
      const savedServiceAccount = await api.rotateApiKey(serviceAccount.id);
      handleSave(savedServiceAccount);
      NotificationManager.success(<>API key for <T noTranslate>{savedServiceAccount.userName}</T> was rotated.</>)
    }
    catch (e) {
      console.error(e);
      NotificationManager.error('Failed to rotate API key. Please try again.');
    }
  }, []);

  const handleDeactivate = useCallback(async (serviceAccount) => {
    if (!window.confirm(`Are you sure you want to deactivate ${serviceAccount.userName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.deleteServiceAccount(serviceAccount.id);
      setServiceAccounts(serviceAccounts => serviceAccounts.filter(s => s.id !== serviceAccount.id));
      NotificationManager.success(<><T noTranslate>{serviceAccount.userName}</T> was deactivated.</>)
    }
    catch (e) {
      console.error(e);
      NotificationManager.error('Failed to delete service account. Please try again.');
    }
  }, []);

  const filteredAndSortedServiceAccounts = serviceAccounts
    .filter(serviceAccount => search(serviceAccount.userName, filter))
    .sort(alphaSort('asc', serviceAccount => serviceAccount.userName));

  let docsLink = multiAz.getCurrentZone().replace("api.", "docs.");

  if (docsLink.includes("localhost")) {
    docsLink = "https://docs.test.agyleintelligence.com"
  }

  return (
    <>
      <ListScreen
        title="Admin"
        activeLink="Admin"
        toolbarActions={[
          <a href={docsLink} target="_blank" rel="noreferrer">API Documentation <Icon name="external-link" /></a>
        ]}
        placeholder="Search service accounts..."
        search={filter}
        onSearch={handleSearch}
        tabs={<AdminTabs />}
        loading={loading && <Loading fullScreen />}
        error={error && <Error fullScreen />}
        primaryAction={<Button to="/admin/service-accounts/create">Create Service Account</Button>}
        items={filteredAndSortedServiceAccounts}
        filter={filter}
        empty={
          serviceAccounts.length < 1 ? (
            <p>No service accounts have been created yet. Click <b>Create Service Account</b> to get started.</p>
          ) : (
            <p>No service accounts found.</p>
          )
        }
      >
        {(serviceAccount, i) => (
          <ListItem
            first={i === 0}
            options={[
              {title: 'Edit', to: `/admin/service-accounts/${serviceAccount.id}`},
              {title: 'Rotate Key', onClick: () => handleRotateKey(serviceAccount)},
              {title: 'Deactivate', onClick: () => handleDeactivate(serviceAccount)}
            ]}
          >
            <ServiceAccountContainer>
              <Identity>
                <Username>
                  <T noTranslate>{serviceAccount.userName}</T>
                  <RoleBadge>{getRoleFriendlyName(serviceAccount.role)}</RoleBadge>
                </Username>
                <LastUsed>
                  {serviceAccount.lastUsed ? (
                    <>Last used <TimeAgo date={serviceAccount.lastUsed} /></>
                  ) : (
                    <>Never used</>
                  )}
                </LastUsed>
              </Identity>
              <ApiKey serviceAccount={serviceAccount} />
            </ServiceAccountContainer>
          </ListItem>
        )}
      </ListScreen>
      <Route
        path="/admin/service-accounts/:id?"
        component={(props) => <ManageServiceAccountModal {...props} onSave={handleSave} />}
      />
    </>
  );
}

type ApiKeyProps = {
  serviceAccount: ServiceAccount;
}

function ApiKey({serviceAccount}: ApiKeyProps) {
  const [showKey, toggleShowKey] = useToggle();
  const api = useApi();
  const [getApiKey, {loading, error, result: key}] = useAsync(api.getApiKey);

  useEffect(() => {
    if (error) {
      NotificationManager.error(`Failed to load the API key for ${serviceAccount.userName}`);
    }
  }, [error]);

  const handleToggleKey = useCallback(() => {
    if (!loading && !key) {
      getApiKey(serviceAccount.id);
    }

    toggleShowKey();
  }, [serviceAccount.id, key, loading]);

  return (
    <ApiKeyContainer>
      <Input inline disabled={!showKey || !key} value={key && showKey ? key.apiKey : serviceAccount.apiKey} />
      <Button tertiary onClick={handleToggleKey} disabled={loading}>
        <Icon name={showKey ? 'eye-off' : 'eye'} />
      </Button>
    </ApiKeyContainer>
  );
}

type ManageServiceAccountModalProps = {
  history: any;
  match: any;
  onSave: (serviceAccount: ServiceAccount) => void;
}

function ManageServiceAccountModal({history, match: {params: {id}}, onSave}: ManageServiceAccountModalProps) {
  const isNewServiceAccount = id === 'create';

  const user = useUser();
  const api = useApi();
  const [getServiceAccount, {loading: loadingServiceAccounts, error: errorLoadingServiceAccounts, result}] = useAsync(api.getServiceAccount);
  const [getUserGroups, {loading: loadingUserGroups, error: errorLoadingGroups, result: groups}] = useAsync(api.getUserGroups);
  const [serviceAccount, setServiceAccount] = useState(createServiceAccount());
  const [saving, setSaving] = useState(false);
  const [reviewerError, setReviewerError] = useState(null);
  const [duplicateUsername, setDuplicateUsername] = useState(null);
  const [validationEnabled, setValidationEnabled] = useState(!isNewServiceAccount);

  useEffect(() => {
    getUserGroups();
  }, []);

  useEffect(() => {
    if (id && !isNewServiceAccount) {
      getServiceAccount(id);
    }
  }, [id, isNewServiceAccount]);

  useEffect(() => {
    setServiceAccount(result || createServiceAccount());
  }, [result]);

  const set = useCallback((property: string, value: string) => {
    setServiceAccount(serviceAccount => ({
      ...serviceAccount,
      [property]: value,
    }));
  }, []);

  const handleClose = useCallback(() => {
    history.push(`/admin/service-accounts`);
  }, []);

  const isValidUserName = validation.create((value, {trim, isEmpty}) => !isEmpty(value)
    && value.endsWith(`@${user.company.namespace}`)
    && value.length > `@${user.company.namespace}`.length);

  const errors = {};

  if (!isValidUserName(serviceAccount.userName)) {
    errors.userName = `Service account username must end with @${user.company.namespace}`;
  }
  else if (serviceAccount.userName && duplicateUsername === serviceAccount.userName) {
    errors.userName = `This username is already in use - please try another.`;
  }

  const hasErrors = Object.keys(errors).filter(key => errors[key]).length > 0;

  const handleSubmit = useCallback(async () => {
    setValidationEnabled(true);

    if (hasErrors) {
      return;
    }

    setSaving(true);
    setDuplicateUsername(null);
    setReviewerError(null);

    try {
      const savedServiceAccount = await api.saveServiceAccount(serviceAccount);
      NotificationManager.success(<T isolate><T variable="username">{savedServiceAccount.userName}</T> was saved</T>);
      onSave(savedServiceAccount);
      history.push('/admin/service-accounts')
    }
    catch (e) {
      if (e.status === 409) {
        const response = await e.json();

        if (response.field === 'username') {
          setDuplicateUsername(serviceAccount.userName);
        } else {
          setReviewerError(response.body);
        }
      }
      else {
        console.error(e);
        NotificationManager.error('Unable to save service account. Please try again.');
      }
    }
    finally {
      setSaving(false);
    }
  }, [serviceAccount, credentials, onSave, hasErrors, validationEnabled]);

  const loading = loadingServiceAccounts || loadingUserGroups;
  const error = errorLoadingServiceAccounts || errorLoadingGroups;
  const userGroups = serviceAccount.groups || [];

  userGroups.forEach(group => group.toString = () => group.name);
  (groups || []).forEach(group => group.toString = () => group.name);

  return (
    <Form onSubmit={handleSubmit}>
      <SideModal
        open={typeof id !== "undefined"}
        title={`${isNewServiceAccount ? "Create" : "Edit"} Service Account`}
        onClose={handleClose}
        options={[
          <Button type="submit" disabled={saving || (validationEnabled && hasErrors)}>{saving ? 'Saving...' : 'Save Service Account'}</Button>,
          <Button secondary onClick={handleClose}>Cancel</Button>
        ]}>
        {loading ? (
          <Loading fullScreen />
        ) : error ? (
          <Error fullScreen />
        ) : (
          <SideModal.Content>
            <Form.Group>
              <Input
                type="text"
                id="username"
                label="Service Account Name"
                value={serviceAccount.userName}
                onChange={e => set('userName', e.target.value)}
                invalid={validationEnabled && errors.userName}
                validationText={validationEnabled && errors.userName}
              />
            </Form.Group>
            <Form.Group>
              <Input
                type="select"
                id="role"
                label="Role"
                helpText={getRoleHelpText(user.role)}
                invalid={reviewerError}
                value={serviceAccount.role || ''}
                onChange={e => set('role', e.target.value)}>
                {getRoleOptions(user.role).map((item, index) =>
                  <option key={index} value={Object.keys(item)[0]}>{Object.values(item)[0]}</option>)}
              </Input>
              {reviewerError && (
                <Notification error>
                  To change this service account's role, remove it as a <strong>reviewer</strong> from the following forms:
                  <ul>
                    {reviewerError.map(form => (
                      <li key={form.id}><Link to={`/forms/${form.id}`} target="_blank" rel="noreferrer">{form.name}</Link></li>
                    ))}
                  </ul>
                </Notification>
              )}
            </Form.Group>
            <Form.Group>
              <ChipInput
                id="group-members"
                placeholder="Enter a user group"
                helpText="Select the groups this service account will belong to"
                label="User Groups"
                value={userGroups}
                suggestions={groups}
                onChange={userGroups => set('groups', userGroups)}
              />
            </Form.Group>
          </SideModal.Content>
        )}
      </SideModal>
    </Form>
  );
}

function createServiceAccount(): ServiceAccount {
  return {
    userName: '',
    groups: [],
    role: Role.USER,
  };
}

const ServiceAccountContainer = styled.div`
  display: flex;

  @media (max-width: 1000px) {
    flex-direction: column;
  }
`;

const Identity = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const Username = styled.strong`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RoleBadge = styled.span`
  font-size: 0.75rem;
  background: pink;
  border: 1px solid red;
  padding: 2px 4px;
  border-radius: 4px;
  margin-left: 4px;
  color: #484848;
  background: rgba(0, 73, 238, 0.05);
  border: 1px solid rgba(0, 73, 238, 0.2);
  font-weight: 400;
`;

const LastUsed = styled.small`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ApiKeyContainer = styled.div`
  flex-direction: row;
  margin-right: 1rem;
`;